import { useState, useRef, useEffect, useCallback } from 'react'
import { MessageBubble } from './MessageBubble'
import type { WSClient } from '@/lib/ws-client'
import type { ChatMessage } from '@/types/brain'

interface ChatWindowProps {
  wsClient: WSClient | null
  connected: boolean
  sessionId: string | null
  onSessionCreated: (id: string) => void
}

export function ChatWindow({ wsClient, connected, sessionId, onSessionCreated }: ChatWindowProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [activeTools, setActiveTools] = useState<{ name: string; status: 'running' | 'done' }[]>([])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const activeToolsRef = useRef(activeTools)

  // Keep ref in sync so the WS handler reads latest tools
  useEffect(() => {
    activeToolsRef.current = activeTools
  }, [activeTools])

  // Auto-scroll on new content
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingContent])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Subscribe to WebSocket messages
  useEffect(() => {
    if (!wsClient) return

    const unsubscribe = wsClient.on('message', (data: Record<string, unknown>) => {
      const type = data.type as string

      switch (type) {
        case 'connected': {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: 'system',
              content: (data.message as string) || 'Brain server ready',
              timestamp: new Date(),
            },
          ])
          break
        }

        case 'session_created': {
          onSessionCreated(data.sessionId as string)
          break
        }

        case 'stream': {
          const event = data.event as Record<string, unknown> | undefined
          if (!event) break

          if (event.type === 'assistant') {
            const message = event.message as Record<string, unknown> | undefined
            const content = message?.content as Array<Record<string, unknown>> | undefined
            if (content) {
              for (const block of content) {
                if (block.type === 'text' && block.text) {
                  setStreamingContent((prev) => prev + (block.text as string))
                }
                if (block.type === 'tool_use' && block.name) {
                  setActiveTools((prev) => {
                    if (prev.find((t) => t.name === block.name)) return prev
                    return [...prev, { name: block.name as string, status: 'running' }]
                  })
                }
              }
            }
          }

          // Tool result -- mark tools as done
          if (
            event.type === 'tool_result' ||
            (event.type === 'system' && event.subtype === 'tool_result')
          ) {
            setActiveTools((prev) => prev.map((t) => ({ ...t, status: 'done' as const })))
          }
          break
        }

        case 'complete': {
          setStreamingContent((prev) => {
            if (prev.trim()) {
              const toolsCopy = [...activeToolsRef.current]
              setMessages((msgs) => [
                ...msgs,
                {
                  id: crypto.randomUUID(),
                  role: 'assistant',
                  content: prev,
                  toolUse: toolsCopy.length > 0 ? toolsCopy : undefined,
                  timestamp: new Date(),
                },
              ])
            }
            return ''
          })
          setActiveTools([])
          setIsStreaming(false)
          break
        }

        case 'error': {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: 'system',
              content: `Error: ${data.message as string}`,
              timestamp: new Date(),
            },
          ])
          setIsStreaming(false)
          break
        }
      }
    })

    return unsubscribe
  }, [wsClient, onSessionCreated])

  const sendMessage = useCallback(() => {
    if (!input.trim() || !wsClient?.connected || isStreaming) return

    const prompt = input.trim()
    setInput('')

    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'user',
        content: prompt,
        timestamp: new Date(),
      },
    ])

    setIsStreaming(true)
    setStreamingContent('')
    setActiveTools([])

    wsClient.send({
      type: 'chat',
      prompt,
      sessionId: sessionId || undefined,
    })
  }, [input, wsClient, isStreaming, sessionId])

  const cancelStream = useCallback(() => {
    wsClient?.send({ type: 'cancel' })
  }, [wsClient])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        sendMessage()
      }
    },
    [sendMessage],
  )

  const isEmpty = messages.length === 0 && !isStreaming

  return (
    <div className="flex flex-col h-full flex-1 min-h-0">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto py-4">
        {isEmpty && (
          <div className="flex items-center justify-center flex-1 h-full flex-col gap-3">
            <div className="w-12 h-12 bg-swiss-black flex items-center justify-center">
              <span className="text-swiss-white font-swiss-sans font-bold text-xl">D</span>
            </div>
            <div className="text-swiss-body text-swiss-gray-500 font-swiss-sans">
              {connected ? 'Send a message to start' : 'Connecting...'}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}

        {/* Streaming bubble */}
        {isStreaming && streamingContent && (
          <div className="flex justify-start px-4 py-1">
            <div className="bg-swiss-off-white border border-swiss-gray-200 px-4 py-3 max-w-[70%] text-swiss-body font-swiss-sans whitespace-pre-wrap break-words">
              {activeTools.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {activeTools.map((tool, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2 py-0.5 border border-swiss-gray-200 font-swiss-mono text-swiss-caption text-swiss-gray-500"
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          tool.status === 'running' ? 'bg-swiss-green' : 'bg-swiss-gray-300'
                        }`}
                      />
                      {tool.name}
                    </span>
                  ))}
                </div>
              )}
              {streamingContent}
              <span className="inline-block w-0.5 h-3.5 bg-swiss-black ml-0.5 animate-blink" />
            </div>
          </div>
        )}

        {/* Thinking indicator (streaming started but no content yet) */}
        {isStreaming && !streamingContent && (
          <div className="px-4 py-2 font-swiss-mono text-swiss-caption text-swiss-gray-500">
            Thinking...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-swiss-gray-200 p-3 bg-swiss-white flex gap-2 items-end">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={connected ? 'Message Decisive...' : 'Connecting...'}
          disabled={!connected}
          rows={1}
          className="flex-1 resize-none border border-swiss-gray-200 p-3 text-swiss-body font-swiss-sans focus:outline-none focus:border-swiss-black min-h-[44px] max-h-[120px] disabled:bg-swiss-gray-100"
          aria-label="Chat message input"
        />
        {isStreaming ? (
          <button
            onClick={cancelStream}
            className="px-4 py-2 bg-swiss-red text-swiss-white font-swiss-sans text-swiss-body"
          >
            Stop
          </button>
        ) : (
          <button
            onClick={sendMessage}
            disabled={!connected || !input.trim()}
            className="px-4 py-2 bg-swiss-black text-swiss-white font-swiss-sans text-swiss-body disabled:opacity-50"
          >
            Send
          </button>
        )}
      </div>

      {/* Status bar */}
      <div className="flex items-center gap-2 px-3 py-1 text-swiss-gray-500 border-t border-swiss-gray-100 bg-swiss-white">
        <span
          className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-swiss-green' : 'bg-swiss-red'}`}
        />
        <span className="font-swiss-mono text-swiss-caption">
          {connected ? 'Connected' : 'Disconnected'}
        </span>
        {sessionId && (
          <span className="font-swiss-mono text-swiss-caption text-swiss-gray-500 ml-auto">
            {sessionId.slice(0, 8)}
          </span>
        )}
      </div>
    </div>
  )
}
